import { CSSProperties } from 'react';
import useWidthBreakpoint from '../../../../../hooks/useWidthBreakpoint';
import { useResultData } from '../context';
import { profitColor, tokens } from '../tokens';
import { pick } from '../responsive';
import EditorialHeader from './shared/EditorialHeader';
import CalendarHeatmap from './charts/CalendarHeatmap';
import ProfitHistogram from './charts/ProfitHistogram';
import StreakBar from './charts/StreakBar';

const PERIODS = ['1d', '1w', '1M', '1Y', '2Y', '5Y'];

const Statistics = () => {
  const displayData = useResultData();
  const { breakpoint } = useWidthBreakpoint();

  // The heatmap spans however many years the result actually has history for.
  const heatmapYearCount = Object.keys(displayData.heatmap).length;
  const heatmapLabel =
    heatmapYearCount > 0
      ? `Monthly Profit · ${heatmapYearCount}-year heatmap`
      : 'Monthly Profit heatmap';

  const padding = pick(breakpoint, {
    sm: '4px 0 16px',
    md: '4px 0 20px',
    lg: '4px 0 24px',
  });

  const matrixSplit: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column', gap: 32, marginBottom: 32 },
    md: {
      display: 'grid',
      gridTemplateColumns: '4fr 8fr',
      gap: 32,
      alignItems: 'flex-start',
      marginBottom: 40,
    },
    lg: {
      display: 'grid',
      gridTemplateColumns: '4fr 8fr',
      gap: 64,
      alignItems: 'flex-start',
      marginBottom: 56,
    },
  });

  const matrixRowTemplate = pick(breakpoint, {
    sm: '120px repeat(6, 1fr)',
    md: '140px repeat(6, 1fr)',
    lg: '160px repeat(6, 1fr)',
  });

  const matrixValueSize = pick(breakpoint, { sm: 18, md: 20, lg: 24 });
  const headingSize = pick(breakpoint, { sm: 36, md: 44, lg: 56 });

  const chartsSplit: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 },
    md: {
      display: 'grid',
      gridTemplateColumns: '5fr 7fr',
      gap: 16,
      marginBottom: 24,
    },
    lg: {
      display: 'grid',
      gridTemplateColumns: '5fr 7fr',
      gap: 24,
      marginBottom: 24,
    },
  });

  const matrixRow = (label: string, periods: Record<string, string>) => (
    <div
      key={label}
      style={{
        display: 'grid',
        gridTemplateColumns: matrixRowTemplate,
        alignItems: 'baseline',
        padding: '16px 0',
        borderBottom: tokens.borderFaint,
      }}
    >
      <span style={{ fontSize: 14, color: tokens.textSecondary }}>{label}</span>
      {PERIODS.map((period) => (
        <div
          key={period}
          style={{
            fontSize: matrixValueSize,
            fontFamily: tokens.fontDisplay,
            color: periods[period]
              ? profitColor(periods[period])
              : tokens.textFaint,
            textAlign: 'right',
          }}
        >
          {periods[period] || '—'}
        </div>
      ))}
    </div>
  );

  return (
    <div
      style={{
        background: tokens.backgroundBase,
        padding,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <EditorialHeader number="04" label="The Shape of Profit" />

      <div style={matrixSplit}>
        {/* Left: headline + monthly average */}
        <div>
          <div
            style={{
              fontSize: headingSize,
              fontFamily: tokens.fontDisplay,
              lineHeight: 0.95,
              letterSpacing: '-0.02em',
            }}
          >
            Profit by horizon.
          </div>
          <div
            style={{
              fontSize: 14,
              color: tokens.textTertiary,
              marginTop: 20,
              lineHeight: 1.6,
              maxWidth: 380,
            }}
          >
            {displayData.txCount} transactions, four perspectives.{' '}
            <b style={{ color: tokens.textSecondary }}>Since</b> measures
            cumulative profit looking back to that horizon;{' '}
            <b style={{ color: tokens.textSecondary }}>Per</b> averages over the
            period.
          </div>
          <div
            style={{
              marginTop: 32,
              padding: '20px 0',
              borderTop: tokens.borderFaint,
              borderBottom: tokens.borderFaint,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: tokens.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
              }}
            >
              Monthly Average
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 14,
                marginTop: 8,
              }}
            >
              <span
                style={{
                  fontSize: headingSize,
                  fontFamily: tokens.fontDisplay,
                  color: tokens.green,
                  lineHeight: 0.95,
                }}
              >
                {displayData.monthlyAvgStable}
              </span>
              <span style={{ fontSize: 16, color: tokens.textSecondary }}>
                stable profit per month
              </span>
            </div>
            <div
              style={{
                fontSize: 14,
                color: tokens.textSecondary,
                marginTop: 8,
                fontFamily: tokens.fontMono,
              }}
            >
              ≈ {displayData.totalMonths} months ·{' '}
              {displayData.profitableMonths} profitable
            </div>
          </div>
        </div>

        {/* Right: SINCE / PER tables */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              paddingBottom: 8,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span
              style={{
                fontSize: 16,
                color: tokens.green,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.15em',
                fontWeight: 700,
              }}
            >
              SINCE
            </span>
            <span style={{ fontSize: 14, color: tokens.textSecondary }}>
              cumulative profit looking back
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: matrixRowTemplate,
              alignItems: 'baseline',
              paddingTop: 12,
            }}
          >
            <div />
            {PERIODS.map((period) => (
              <div
                key={period}
                style={{
                  fontSize: 11,
                  color: tokens.textMuted,
                  textAlign: 'right',
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.1em',
                }}
              >
                {period}
              </div>
            ))}
          </div>
          {matrixRow('Stable Profit', displayData.profitPeriods.stable)}
          {matrixRow('Asset Profit', displayData.profitPeriods.asset)}

          <div
            style={{
              marginTop: 28,
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              paddingBottom: 8,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span
              style={{
                fontSize: 16,
                color: tokens.amber,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.15em',
                fontWeight: 700,
              }}
            >
              PER
            </span>
            <span style={{ fontSize: 14, color: tokens.textSecondary }}>
              average profit over each period
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: matrixRowTemplate,
              alignItems: 'baseline',
              paddingTop: 12,
            }}
          >
            <div />
            {PERIODS.map((period) => (
              <div
                key={period}
                style={{
                  fontSize: 11,
                  color: tokens.textMuted,
                  textAlign: 'right',
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.1em',
                }}
              >
                {period}
              </div>
            ))}
          </div>
          {matrixRow('Avg Stable', displayData.profitPeriods.avgStable)}
          {matrixRow('Avg Asset', displayData.profitPeriods.avgAsset)}
        </div>
      </div>

      <div
        style={{
          paddingTop: 32,
          borderTop: '1px double rgba(255,255,255,0.1)',
        }}
      >
        <div style={chartsSplit}>
          <div
            style={{
              background: tokens.backgroundRaised,
              borderRadius: 6,
              padding: '20px 24px',
              overflowX: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 16,
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  color: tokens.textSecondary,
                  fontWeight: 600,
                }}
              >
                {heatmapLabel}
              </span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 10,
                  color: tokens.textFaint,
                }}
              >
                <span>−50%</span>
                <div style={{ display: 'flex', gap: 2 }}>
                  {[
                    tokens.red,
                    'rgba(240,22,47,0.5)',
                    'rgba(240,22,47,0.4)',
                    'rgba(0,176,97,0.4)',
                    'rgba(0,176,97,0.65)',
                    tokens.green,
                  ].map((color, index) => (
                    <div
                      key={index}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        background: color,
                      }}
                    />
                  ))}
                </div>
                <span>+50%</span>
              </div>
            </div>
            <div style={{ textAlign: 'center', paddingBottom: 24 }}>
              <CalendarHeatmap
                cellSize={
                  breakpoint === 'sm' ? 18 : breakpoint === 'md' ? 22 : 28
                }
                gap={3}
              />
            </div>
          </div>

          <div
            style={{
              background: tokens.backgroundRaised,
              borderRadius: 6,
              padding: '20px 24px',
            }}
          >
            <div
              style={{
                fontSize: 16,
                color: tokens.textSecondary,
                marginBottom: 16,
                fontWeight: 600,
              }}
            >
              Per-trade profit distribution
            </div>
            <div style={{ height: 200 }}>
              <ProfitHistogram width={540} height={200} />
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: pick(breakpoint, {
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                  lg: 'repeat(4, 1fr)',
                }),
                gap: '14px 12px',
                marginTop: 16,
                paddingTop: 12,
                borderTop: tokens.borderFaint,
              }}
            >
              {[
                ['MEDIAN', displayData.medianProfit, tokens.green],
                ['BEST', displayData.bestTrade, tokens.green],
                ['WORST', displayData.worstTrade, tokens.red],
                ['WIN RATE', displayData.winRate, tokens.textPrimary],
                ['CORE', `${displayData.coreCount}`, tokens.textPrimary],
                [
                  'TAKE PROFIT',
                  `${displayData.takeProfits}`,
                  tokens.textPrimary,
                ],
                ['STOP LOSS', `${displayData.stopLosses}`, tokens.textPrimary],
                ['AVG DURATION', displayData.avgDuration, tokens.textPrimary],
              ].map(([label, value, color]) => (
                <div key={label as string}>
                  <div
                    style={{
                      fontSize: 13,
                      color: tokens.textSecondary,
                      fontFamily: tokens.fontMono,
                      letterSpacing: '0.1em',
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontFamily: tokens.fontDisplay,
                      color: color as string,
                      marginTop: 2,
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{
            background: tokens.backgroundRaised,
            borderRadius: 6,
            padding: '20px 24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 12,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 16,
                color: tokens.textSecondary,
                fontWeight: 600,
              }}
            >
              Win / Loss sequence · all {displayData.txCount} trades
              chronologically
            </span>
            <span style={{ fontSize: 12, color: tokens.textMuted }}>
              <b style={{ color: tokens.green }}>{displayData.wins}W</b> /{' '}
              <b style={{ color: tokens.red }}>{displayData.losses}L</b> · win
              rate {displayData.winRate}
            </span>
          </div>
          <div style={{ height: 60 }}>
            <StreakBar width={1272} height={60} />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: pick(breakpoint, {
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(4, 1fr)',
              }),
              gap: 16,
              marginTop: 16,
              paddingTop: 12,
              borderTop: tokens.borderFaint,
              fontFamily: tokens.fontMono,
            }}
          >
            {[
              [
                'Longest win streak',
                `${displayData.longestWinStreak}`,
                tokens.green,
              ],
              [
                'Longest loss streak',
                `${displayData.longestLossStreak}`,
                tokens.red,
              ],
              ['Avg streak length', displayData.avgStreak, tokens.textPrimary],
              ['Avg trade profit', displayData.avgProfit, tokens.green],
            ].map(([label, value, color]) => (
              <div key={label as string}>
                <div
                  style={{
                    fontSize: 13,
                    color: tokens.textSecondary,
                    letterSpacing: '0.1em',
                    fontWeight: 700,
                  }}
                >
                  {(label as string).toUpperCase()}
                </div>
                <div
                  style={{
                    fontSize: 24,
                    color: color as string,
                    fontWeight: 700,
                    marginTop: 2,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
